class BlingBulkQuote extends HTMLElement {
  constructor() {
    super();
    this.button = null;
    this.config = null;
    this.drawer = null;
    this.productData = null;
  }

  connectedCallback() {
    this.button = this.querySelector('.bling-bq-btn');
    const configId = this.getAttribute('data-config');
    const configScript = configId ? document.getElementById(configId) : this.querySelector('script[type="application/json"]');
    if (configScript) {
      this.config = JSON.parse(configScript.textContent);
    } else {
      console.error('Bling Bulk Quote: Config script not found');
    }

    if (this.button) {
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer();
      });
    }
  }

  async openDrawer() {
    // 1. Fetch Shopify Product metadata dynamically to ensure variant details are precise
    if (!this.productData) {
      try {
        this.productData = await fetch(`/products/${this.config.productHandle}.js`).then((r) => r.json());
      } catch (err) {
        console.error('Failed to fetch product JSON:', err);
      }
    }

    // 2. Identify the active selected variant ID on the page
    const variantInput = document.querySelector('form[action*="/cart/add"] input[name="id"], input[name="id"]');
    const selectedVariantId = variantInput ? variantInput.value : '';

    let variantTitle = '';
    let variantImageUrl = '';

    if (this.productData) {
      const selectedVariant = this.productData.variants.find((v) => String(v.id) === String(selectedVariantId)) || this.productData.variants[0];
      if (selectedVariant) {
        variantTitle = selectedVariant.title !== 'Default Title' ? selectedVariant.title : '';
        variantImageUrl = selectedVariant.featured_image ? selectedVariant.featured_image.src : (this.productData.featured_image || '');
      }
    }

    // 3. Get quantity currently set in page quantity input
    const quantityInput = document.querySelector('input[name="quantity"]');
    const pageQuantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

    // 4. Create the drawer element if it does not exist in the DOM
    if (!this.drawer) {
      this.createDrawerDOM();
    }
    
    // Apply user selected border-radius setting to the drawer
    this.drawer.style.setProperty('--bling-bq-drawer-radius', (this.config.borderRadius || 0) + 'px');

    // 5. Update drawer with product & variant context details
    const imgEl = this.drawer.querySelector('.bling-bq-drawer__product-img');
    if (variantImageUrl) {
      imgEl.src = variantImageUrl;
      imgEl.alt = this.config.productTitle;
      imgEl.style.display = 'block';
    } else {
      imgEl.style.display = 'none';
    }

    const titleEl = this.drawer.querySelector('.bling-bq-drawer__product-title');
    titleEl.textContent = this.config.productTitle;

    const variantEl = this.drawer.querySelector('.bling-bq-drawer__product-variant');
    if (variantTitle) {
      variantEl.textContent = `Variant: ${variantTitle}`;
      variantEl.style.display = 'block';
    } else {
      variantEl.style.display = 'none';
    }

    // Pre-populate quantity
    const qtyInput = this.drawer.querySelector('#bq-qty');
    qtyInput.value = pageQuantity > 0 ? pageQuantity : 1;

    // Set variant data attributes
    const form = this.drawer.querySelector('.bling-bq-form');
    form.setAttribute('data-variant-id', selectedVariantId);
    form.setAttribute('data-variant-title', variantTitle);

    // Reset Form view state
    form.reset();
    const fieldsWrapper = form.querySelector('.bling-bq-fields-wrapper');
    if (fieldsWrapper) fieldsWrapper.style.display = 'block';
    const footer = form.querySelector('.bling-bq-drawer-footer');
    if (footer) footer.style.display = 'block';

    this.drawer.querySelector('.bling-bq-success-view').style.display = 'none';
    this.drawer.querySelector('.bling-bq-error-msg').style.display = 'none';
    this.drawer.querySelector('.bling-bq-error-msg').textContent = '';

    // 6. Open the drawer with transition classes
    document.body.style.overflow = 'hidden'; // prevent page scrolling
    this.drawer.style.display = 'flex';
    setTimeout(() => {
      this.drawer.classList.add('active');
    }, 10);
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.remove('active');
    document.body.style.overflow = '';
    setTimeout(() => {
      this.drawer.style.display = 'none';
    }, 300); // matches transition time
  }

  createDrawerDOM() {
    this.drawer = document.createElement('div');
    this.drawer.className = 'bling-bq-drawer-wrapper';
    this.drawer.style.display = 'none';

    this.drawer.innerHTML = `
      <div class="bling-bq-drawer-overlay"></div>
      <div class="bling-bq-drawer-inner" style="display: flex; flex-direction: column; height: 100%;">
        <div class="bling-bq-drawer-header" style="flex-shrink: 0;">
          <h2 class="bling-bq-drawer-title">Bulk Quote Request</h2>
          <button type="button" class="bling-bq-drawer-close" aria-label="Close drawer">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <form class="bling-bq-form" style="display: flex; flex-direction: column; flex: 1; height: calc(100% - 70px); overflow: hidden; margin: 0;">
          <div class="bling-bq-drawer-body" style="flex: 1; overflow-y: auto; padding: 24px;">
            <div class="bling-bq-drawer-product-info">
              <img class="bling-bq-drawer__product-img" src="" alt="" style="display: none;" />
              <div class="bling-bq-drawer__product-details">
                <div class="bling-bq-drawer__product-title"></div>
                <div class="bling-bq-drawer__product-variant" style="display: none;"></div>
              </div>
            </div>

            <div class="bling-bq-fields-wrapper">
              <div class="bling-bq-field">
                <label class="bling-bq-label" for="bq-name">Full Name <span class="required-star">*</span></label>
                <input type="text" id="bq-name" name="name" class="bling-bq-input field__input" required placeholder="e.g. John Smith" />
              </div>

              <div class="bling-bq-field">
                <label class="bling-bq-label" for="bq-phone">Phone Number <span class="required-star">*</span></label>
                <input type="tel" id="bq-phone" name="phone" class="bling-bq-input field__input" required placeholder="e.g. +1 (555) 019-2834" />
              </div>

              <div class="bling-bq-field">
                <label class="bling-bq-label" for="bq-email">Email Address</label>
                <input type="email" id="bq-email" name="email" class="bling-bq-input field__input" placeholder="e.g. john@example.com" />
              </div>

              <div class="bling-bq-field">
                <label class="bling-bq-label" for="bq-company">Company Name</label>
                <input type="text" id="bq-company" name="company" class="bling-bq-input field__input" placeholder="e.g. Acme Corporation" />
              </div>

              <div class="bling-bq-field">
                <label class="bling-bq-label" for="bq-qty">Quantity Required <span class="required-star">*</span></label>
                <input type="number" id="bq-qty" name="quantity" class="bling-bq-input field__input" required min="1" step="1" />
              </div>

              <div class="bling-bq-field">
                <label class="bling-bq-label" for="bq-message">Additional Information <span class="required-star">*</span></label>
                <textarea id="bq-message" name="message" class="bling-bq-textarea field__input" rows="4" required placeholder="Describe your packaging requirements, custom styling, or delivery details..."></textarea>
              </div>

              <div class="bling-bq-error-msg" style="display: none;"></div>
            </div>

            <div class="bling-bq-success-view" style="display: none;">
              <div class="success-icon" style="display: flex; justify-content: center; align-items: center; width: 100%;">
                <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check-circle-2"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="m9 12 2 2 4-4"/></svg>
              </div>
              <h3 class="success-title">Inquiry Submitted</h3>
              <p class="success-message">Thank you for your bulk order inquiry. Our wholesale support team will review your custom specifications and reach out to you shortly.</p>
              <button type="button" class="bling-bq-close-success-btn button button--secondary">Close Drawer</button>
            </div>
          </div>

          <div class="bling-bq-drawer-footer" style="padding: 16px 24px; border-top: 1px solid var(--color-border, rgba(18, 18, 18, 0.08)); background: var(--color-background, #ffffff); flex-shrink: 0;">
            <button type="submit" class="bling-bq-submit-btn button button--primary" style="width: 100%;">
              <span class="btn-text">Submit Inquiry</span>
              <span class="btn-spinner" style="display: none;">
                <svg class="spinner-svg" viewBox="0 0 50 50"><circle class="path" cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="4"></circle></svg>
              </span>
            </button>
          </div>
        </form>
      </div>
    `;

    document.body.appendChild(this.drawer);

    // Event Listeners for closing the drawer
    this.drawer.querySelector('.bling-bq-drawer-close').addEventListener('click', () => this.closeDrawer());
    this.drawer.querySelector('.bling-bq-drawer-overlay').addEventListener('click', () => this.closeDrawer());
    this.drawer.querySelector('.bling-bq-close-success-btn').addEventListener('click', () => this.closeDrawer());

    // Submit handler
    const form = this.drawer.querySelector('.bling-bq-form');
    form.addEventListener('submit', (e) => this.handleFormSubmit(e));
  }

  async handleFormSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const submitBtn = form.querySelector('.bling-bq-submit-btn');
    const btnText = submitBtn.querySelector('.btn-text');
    const btnSpinner = submitBtn.querySelector('.btn-spinner');
    const errorEl = this.drawer.querySelector('.bling-bq-error-msg');

    errorEl.style.display = 'none';
    errorEl.textContent = '';

    // Collect values
    const name = form.querySelector('#bq-name').value.trim();
    const phone = form.querySelector('#bq-phone').value.trim();
    const email = form.querySelector('#bq-email').value.trim();
    const company = form.querySelector('#bq-company').value.trim();
    const quantity = parseInt(form.querySelector('#bq-qty').value, 10);
    const message = form.querySelector('#bq-message').value.trim();

    // Validations
    if (!name) {
      this.showError('Full Name is required.');
      return;
    }
    if (!phone || phone.length < 7) {
      this.showError('Please enter a valid phone number (minimum 7 characters).');
      return;
    }
    if (isNaN(quantity) || quantity <= 0) {
      this.showError('Please enter a positive quantity.');
      return;
    }
    if (!message) {
      this.showError('Please provide some additional details regarding your bulk order inquiry.');
      return;
    }

    // Set Loading State
    submitBtn.disabled = true;
    btnText.style.opacity = '0.3';
    btnSpinner.style.display = 'block';

    const payload = {
      customer_name: name,
      customer_phone: phone,
      customer_email: email || undefined,
      company_name: company || undefined,
      product_id: String(this.config.productId),
      product_title: this.config.productTitle,
      product_handle: this.config.productHandle,
      variant_id: form.getAttribute('data-variant-id') || undefined,
      variant_title: form.getAttribute('data-variant-title') || undefined,
      quantity,
      message,
    };

    try {
      const baseUrl = (this.config.apiBase || 'https://theblingglobal.in').replace(/\/+$/, '');
      const response = await fetch(`${baseUrl}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit quote inquiry.');
      }

      // Success view transition
      const fieldsWrapper = form.querySelector('.bling-bq-fields-wrapper');
      if (fieldsWrapper) fieldsWrapper.style.display = 'none';
      const footer = form.querySelector('.bling-bq-drawer-footer');
      if (footer) footer.style.display = 'none';
      
      const successView = this.drawer.querySelector('.bling-bq-success-view');
      successView.style.display = 'flex';
    } catch (err) {
      console.error('Submission error:', err);
      this.showError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      // Revert loading state
      submitBtn.disabled = false;
      btnText.style.opacity = '1';
      btnSpinner.style.display = 'none';
    }
  }

  showError(message) {
    const errorEl = this.drawer.querySelector('.bling-bq-error-msg');
    errorEl.textContent = message;
    errorEl.style.display = 'block';
    errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

customElements.define('bling-bulk-quote', BlingBulkQuote);
